import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Loader2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export function useSelection(items, idKey = "id") {
  const [selected, setSelected] = useState(() => new Set());
  const ids = (items || []).map((x) => x[idKey]);
  const allChecked = ids.length > 0 && ids.every((id) => selected.has(id));
  const toggle = (id) =>
    setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () =>
    setSelected((p) => (ids.every((id) => p.has(id)) ? new Set() : new Set(ids)));
  const clear = () => setSelected(new Set());
  return { selected, count: selected.size, allChecked, toggle, toggleAll, clear, ids: [...selected] };
}

export function RowCheck({ checked, onCheck, testid }) {
  return <Checkbox checked={checked} onCheckedChange={onCheck} onClick={(e) => e.stopPropagation()} data-testid={testid} />;
}

export function SelectAllCheck({ checked, onCheck, testid = "bulk-select-all" }) {
  return <Checkbox checked={checked} onCheckedChange={onCheck} data-testid={testid} />;
}

function PreviewList({ p }) {
  return (
    <div className="space-y-2 text-sm">
      <div className="max-h-48 overflow-y-auto border rounded p-2" data-testid="data-preview-records">
        {p.records.map((r) => <div key={r.id} className="py-0.5"><span className="font-medium">{r.label || r.id}</span> <span className="text-xs text-muted-foreground">{r.detail}</span></div>)}
      </div>
      {p.impact.map((t) => <p key={t} className="text-xs text-amber-700" data-testid="data-preview-impact">• {t}</p>)}
      {p.skipped.length > 0 && <p className="text-xs text-muted-foreground" data-testid="data-preview-skipped">
        {p.skipped.length} skipped: {p.skipped.slice(0, 5).map((s) => `${s.label || s.id} (${s.reason})`).join("; ")}</p>}
      <p className="text-xs text-muted-foreground">{p.note}</p>
    </div>
  );
}

/** Preview → confirm (bulk: typed phrase + admin password) for archive or restore. */
export function DataActionDialog({ entity, ids, action = "archive", open, onClose, onDone }) {
  const [p, setP] = useState(null); const [step, setStep] = useState(1);
  const [text, setText] = useState(""); const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { setP(null); setStep(1); setText(""); setPw(""); return; }
    api.post("/admin/data/preview", { entity, ids, action }).then((r) => setP(r.data))
      .catch((e) => { toast.error(fmtErr(e)); onClose(); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const run = async () => {
    setBusy(true);
    try {
      const body = { entity, ids: p.records.map((r) => r.id), confirm_token: p.confirm_token, confirm_text: text, password: pw };
      const { data: d } = await api.post(action === "archive" ? "/admin/bulk-delete" : "/admin/data/restore", body);
      const n = (d.deleted || d.restored || []).length;
      toast.success(`${action === "archive" ? "Archived" : "Restored"} ${n} record(s)` + (d.skipped.length ? ` · ${d.skipped.length} skipped` : ""));
      onDone && onDone(); onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const verb = action === "archive" ? "Archive" : "Restore";
  const bulk = p?.requires_second_confirmation;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="data-action-dialog">
        <DialogHeader><DialogTitle>{verb} {p ? p.records.length : "…"} {entity}</DialogTitle>
          <DialogDescription>Check exactly what will be affected before confirming.</DialogDescription></DialogHeader>
        {!p ? <Loader2 className="h-4 w-4 animate-spin" /> : step === 1 ? <PreviewList p={p} /> : (
          <div className="space-y-2 text-sm" data-testid="data-second-confirm">
            <p>Second confirmation: type <span className="font-mono font-semibold">{p.confirm_phrase}</span> and enter your admin password.</p>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={p.confirm_phrase} data-testid="data-confirm-text" />
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Admin password" data-testid="data-confirm-password" />
          </div>)}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="data-action-cancel">Cancel</Button>
          {p && p.records.length > 0 && (bulk && step === 1
            ? <Button onClick={() => setStep(2)} data-testid="data-action-next">Continue</Button>
            : <Button variant={action === "archive" ? "destructive" : "default"} disabled={busy || (bulk && (text.trim().toUpperCase() !== p.confirm_phrase || !pw))}
                onClick={run} data-testid="data-action-confirm">{busy ? "Working…" : `Confirm ${verb.toLowerCase()}`}</Button>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BulkDeleteBar({ entity, selection, onDone, label = "records" }) {
  const [open, setOpen] = useState(false);
  if (selection.count === 0) return null;
  return (
    <div className="flex items-center gap-3 mb-3 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20" data-testid="bulk-delete-bar">
      <span className="text-sm font-medium" data-testid="bulk-selection-count">{selection.count} {label} selected</span>
      <Button variant="ghost" size="sm" onClick={selection.clear} data-testid="bulk-clear-btn">Clear</Button>
      <Button variant="destructive" size="sm" className="ml-auto" onClick={() => setOpen(true)} data-testid="bulk-delete-btn">
        <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive selected
      </Button>
      <DataActionDialog entity={entity} ids={selection.ids} open={open} onClose={() => setOpen(false)}
        onDone={() => { selection.clear(); onDone && onDone(); }} />
    </div>
  );
}
